import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Holi Colors Canvas Component
function HoliCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            let colorBursts = [];
            let colorParticles = [];
            const floatingColors = [];
            let textParticles = [];

            // Holi color palette
            const holiColors = [
                '#FF1493', // Hot Pink
                '#FF6347', // Red
                '#FFD700', // Yellow
                '#00CED1', // Cyan
                '#9370DB', // Purple
                '#32CD32', // Lime Green
                '#FF69B4', // Pink
                '#FFA500', // Orange
            ];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // Helper: Create Color Burst
            const createColorBurst = (x, y) => {
                const burst = {
                    x,
                    y,
                    color: holiColors[Math.floor(Math.random() * holiColors.length)],
                    particles: [],
                };

                const particleCount = 60;
                for (let i = 0; i < particleCount; i += 1) {
                    const angle = (Math.PI * 2 * i) / particleCount;
                    const speed = Math.random() * 6 + 3;
                    burst.particles.push({
                        x: burst.x,
                        y: burst.y,
                        vx: Math.cos(angle) * speed,
                        vy: Math.sin(angle) * speed,
                        radius: Math.random() * 8 + 3,
                        alpha: 1,
                        decay: Math.random() * 0.015 + 0.01,
                    });
                }
                return burst;
            };

            // Helper: Update Color Burst
            const updateColorBurst = (burst) => {
                burst.particles.forEach((p) => {
                    p.x += p.vx;
                    p.y += p.vy;
                    p.vx *= 0.95;
                    p.vy *= 0.95;
                    p.alpha -= p.decay;
                });
                burst.particles = burst.particles.filter((p) => p.alpha > 0);
            };

            // Helper: Draw Color Burst
            const drawColorBurst = (burst) => {
                burst.particles.forEach((p) => {
                    ctx.save();
                    ctx.globalAlpha = p.alpha;

                    // Create powder effect with gradient
                    const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
                    gradient.addColorStop(0, burst.color);
                    gradient.addColorStop(0.5, burst.color);
                    gradient.addColorStop(1, `${burst.color}00`);

                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                    ctx.fillStyle = gradient;
                    ctx.fill();
                    ctx.restore();
                });
            };

            // Helper: Create Color Particle
            const createColorParticle = () => ({
                x: Math.random() * canvas.width,
                y: canvas.height + 20,
                targetY: Math.random() * canvas.height * 0.6,
                radius: Math.random() * 6 + 2,
                color: holiColors[Math.floor(Math.random() * holiColors.length)],
                speed: Math.random() * 2 + 1,
                alpha: 0.8,
                wobble: Math.random() * Math.PI * 2,
                wobbleSpeed: Math.random() * 0.05 + 0.02,
            });

            // Helper: Update Color Particle
            const updateColorParticle = (particle) => {
                particle.wobble += particle.wobbleSpeed;
                particle.x += Math.sin(particle.wobble) * 2;
                particle.y -= particle.speed;

                return particle.y <= particle.targetY; // Returns true if reached target
            };

            // Helper: Draw Color Particle
            const drawColorParticle = (particle) => {
                ctx.save();
                ctx.globalAlpha = particle.alpha;
                ctx.shadowBlur = 15;
                ctx.shadowColor = particle.color;

                const gradient = ctx.createRadialGradient(particle.x, particle.y, 0, particle.x, particle.y, particle.radius);
                gradient.addColorStop(0, '#FFFFFF');
                gradient.addColorStop(0.3, particle.color);
                gradient.addColorStop(1, `${particle.color}88`);

                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
                ctx.fillStyle = gradient;
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Floating Color
            const createFloatingColor = () => ({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                radius: Math.random() * 3 + 1,
                color: holiColors[Math.floor(Math.random() * holiColors.length)],
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5,
                alpha: Math.random() * 0.5 + 0.3,
            });

            // Helper: Update Floating Color
            const updateFloatingColor = (fc) => {
                fc.x += fc.vx;
                fc.y += fc.vy;

                // Wrap around edges
                if (fc.x < 0) fc.x = canvas.width;
                if (fc.x > canvas.width) fc.x = 0;
                if (fc.y < 0) fc.y = canvas.height;
                if (fc.y > canvas.height) fc.y = 0;
            };

            // Helper: Draw Floating Color
            const drawFloatingColor = (fc) => {
                ctx.save();
                ctx.globalAlpha = fc.alpha;
                ctx.beginPath();
                ctx.arc(fc.x, fc.y, fc.radius, 0, Math.PI * 2);
                ctx.fillStyle = fc.color;
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Text Particle
            const createTextParticle = (x, y, char, index) => ({
                x,
                y: y + 100,
                targetY: y,
                char,
                delay: index * 12,
                time: 0,
                alpha: 0,
                scale: 0,
                color: holiColors[index % holiColors.length],
                wobble: 0,
                wobbleSpeed: 0.08,
                bounceOffset: 0,
                bounceSpeed: 0.1,
            });

            // Helper: Update Text Particle
            const updateTextParticle = (tp) => {
                tp.time += 1;
                if (tp.time > tp.delay) {
                    // Entry animation
                    if (tp.y > tp.targetY) {
                        tp.y -= 8;
                        tp.alpha = Math.min(1, tp.alpha + 0.15);
                        tp.scale = Math.min(1, tp.scale + 0.15);
                    } else {
                        // Bounce animation
                        tp.wobble += tp.wobbleSpeed;
                        tp.bounceOffset = Math.sin(tp.wobble) * 8;
                    }
                }
            };

            // Helper: Draw Text Particle
            const drawTextParticle = (tp) => {
                if (tp.time < tp.delay) return;

                ctx.save();
                ctx.globalAlpha = tp.alpha;
                ctx.font = `bold ${55 * tp.scale}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Outer glow
                ctx.shadowBlur = 20;
                ctx.shadowColor = tp.color;

                // Stroke (outline)
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.strokeText(tp.char, tp.x, tp.y + tp.bounceOffset);

                // Fill
                ctx.fillStyle = tp.color;
                ctx.fillText(tp.char, tp.x, tp.y + tp.bounceOffset);

                ctx.restore();
            };

            // Action: Create bursts
            const createRandomBurst = () => {
                const x = Math.random() * canvas.width;
                const y = Math.random() * canvas.height * 0.7 + canvas.height * 0.15;
                colorBursts.push(createColorBurst(x, y));
            };

            // Action: Create Text
            const createHoliText = () => {
                const text = 'HAPPY HOLI';

                // Match Lohri positioning: Right side (70%) on desktop, centered on mobile
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.20;

                const charSpacing = canvas.width < 768 ? 38 : 52;
                const startX = centerX - (text.length * charSpacing) / 2;

                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    const char = text[i];
                    const charX = startX + i * charSpacing;
                    textParticles.push(createTextParticle(charX, centerY, char, i));
                }
            };
            // Initialize floating colors
            for (let i = 0; i < 80; i += 1) {
                floatingColors.push(createFloatingColor());
            }

            // Initialize text after 1 second
            setTimeout(() => {
                createHoliText();
            }, 50);

            // Create initial bursts
            setTimeout(() => {
                for (let i = 0; i < 3; i += 1) {
                    setTimeout(() => createRandomBurst(), i * 500);
                }
            }, 500);

            // Animation loop
            const animate = () => {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Random color bursts
                if (Math.random() < 0.02) {
                    createRandomBurst();
                }

                // Random rising color particles
                if (Math.random() < 0.08) {
                    colorParticles.push(createColorParticle());
                }

                // Update and draw floating colors
                floatingColors.forEach((fc) => {
                    updateFloatingColor(fc);
                    drawFloatingColor(fc);
                });

                // Update and draw color bursts
                colorBursts = colorBursts.filter((burst) => {
                    updateColorBurst(burst);
                    drawColorBurst(burst);
                    return burst.particles.length > 0;
                });

                // Update and draw color particles
                colorParticles = colorParticles.filter((particle) => {
                    drawColorParticle(particle);
                    const reached = updateColorParticle(particle);
                    if (reached) {
                        if (Math.random() < 0.3) {
                            colorBursts.push(createColorBurst(particle.x, particle.y));
                        }
                        return false;
                    }
                    return true;
                });

                // Update and draw text
                textParticles.forEach((tp) => {
                    updateTextParticle(tp);
                    drawTextParticle(tp);
                });

                animationFrameId = requestAnimationFrame(animate);
            };

            animate();

            const handleResize = () => {
                resizeCanvas();
                if (textParticles.length > 0) {
                    createHoliText();
                }
            };
            window.addEventListener('resize', handleResize);

            // Cleanup
            return () => {
                window.removeEventListener('resize', handleResize);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
            }}
        />
    );
}

export default function HoliTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            backgroundImage: 'url(/assets/background/festival_holi_bg.png)',
            backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
            overflow: 'hidden',
            '&:before': {
                content: '""', position: 'absolute', inset: 0,
                background: `linear-gradient(135deg, ${alpha('#FF1493', 0.1)}, ${alpha('#00CED1', 0.1)})`,
                zIndex: 0,
            },
        }}>
            <HoliCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(10,5,15,0.96)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha('#FF1493', 0.35)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.4)}`,
                    `0 16px 64px ${alpha('#FF1493', 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: 'linear-gradient(90deg, transparent, #FF1493, #FFD700, #00CED1, transparent)',
                    borderRadius: '0 0 4px 4px',
                    animation: 'holiGlow 3s ease-in-out infinite',
                },
                '@keyframes holiGlow': { '0%, 100%': { opacity: 0.7 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: 'linear-gradient(90deg, #FF1493, #FFD700, #00CED1, #FF1493)',
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha('#FF1493', 0.5)}`,
                    }}>🎨 &nbsp; Happy Holi &nbsp; 🎨</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2 }}><Logo /></Stack>
                {cloneElement(children, { hideHelpLink: true })}
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha('#FF1493', 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

HoliTheme.propTypes = {
    children: PropTypes.node,
};
